// The calendar's data: public/games.json, one entry per door. The number of doors comes from the
// file. A door opens on its release date in Swedish time; on preview hosts (the test address and
// local development) the doors listed in preview.open are open already.
//
// Milestone 4 moves the date filtering to a server function, so unreleased games are not in what
// the browser receives (D7). The server keeps every door (day and release date, so the calendar
// keeps its shape) and leaves out `game` for doors that have not opened; it also decides the
// preview rule. The static public/games.json must then not be shipped.

export interface Game {
  id: string;
  title: string;
  year: number;
  url: string;
}

export interface DoorData {
  day: number;
  /** YYYY-MM-DD, Swedish time */
  release: string;
  game?: Game;
}

export interface Calendar {
  season: number;
  timezone: string;
  preview?: { hosts: string[]; open: number[] };
  doors: DoorData[];
}

/** What a door shows today. */
export type DoorState = "locked" | "open" | "empty";

export interface Door extends DoorData {
  state: DoorState;
  /** the door for today's date */
  today: boolean;
}

export async function loadCalendar(url = "./games.json"): Promise<Calendar> {
  const r = await fetch(url, { cache: "no-cache" });
  if (!r.ok) throw new Error(`games.json: HTTP ${r.status}`);
  const c = (await r.json()) as Calendar;
  if (!Array.isArray(c.doors)) throw new Error("games.json: no doors");
  c.doors.sort((a, b) => a.day - b.day);
  return c;
}

/** Today's date as YYYY-MM-DD in the calendar's time zone. */
export function todayIn(timezone: string, now = new Date()): string {
  // sv-SE formats as YYYY-MM-DD
  return new Intl.DateTimeFormat("sv-SE", { timeZone: timezone, year: "numeric", month: "2-digit", day: "2-digit" }).format(now);
}

/**
 * Door states for today. The server (the Worker, or Vite in development) has already decided what
 * is open: a door that has opened comes with its game, one that has not comes without. So a game
 * means open; without a game, a door whose date has passed is empty ("coming soon") and the rest
 * are locked. The preview rule lives on the server only.
 */
export function doorStates(c: Calendar, now = new Date()): Door[] {
  const today = todayIn(c.timezone, now);
  return c.doors.map((d) => {
    const state: DoorState = d.game ? "open" : d.release <= today ? "empty" : "locked";
    return { ...d, state, today: d.release === today };
  });
}

/**
 * Milliseconds until the calendar date next changes in the calendar's time zone (plus a second's
 * margin). Found by searching for the instant the date changes, so days of 23 or 25 hours (the
 * DST changes) come out right.
 */
export function msToNextMidnight(timezone: string, now = new Date()): number {
  const today = todayIn(timezone, now);
  let lo = now.getTime();
  let hi = lo + 26 * 3600_000; // the next date change is always within 26 hours
  if (todayIn(timezone, new Date(hi)) === today) return 3600_000; // should not happen; re-check in an hour
  while (hi - lo > 1000) {
    const mid = Math.floor((lo + hi) / 2);
    if (todayIn(timezone, new Date(mid)) === today) lo = mid;
    else hi = mid;
  }
  return Math.max(1000, hi - now.getTime() + 1000);
}

/** "5 december" for a YYYY-MM-DD date. */
export function swedishDate(iso: string): string {
  const [, m, d] = iso.split("-").map(Number);
  const months = ["januari", "februari", "mars", "april", "maj", "juni", "juli", "augusti", "september", "oktober", "november", "december"];
  return `${d} ${months[m - 1]}`;
}
